import type React from "react"
import { useMemo } from "react"
import { NwssSignalDrawer } from "../NwssSignalDrawer"
import {
  DEFAULT_NWSS_SORT_MODEL,
  PRECOMPUTED_MAP_RESULT_LIMIT,
} from "../constants"
import { getBiothreatV2NwssColumns } from "./BiothreatV2Columns"
import { BiothreatV2NwssFilters } from "./BiothreatV2Filters"
import { NWSS_SORTABLE_COLUMNS } from "../PrecomputedSortableColumns"
import { usePrecomputedBiothreatSignals } from "../PrecomputedBiothreatSignalsContext"
import { usePrecomputedNwssSignals } from "../usePrecomputedNwssSignals"
import {
  usePrecomputedGridPage,
  usePrecomputedGridPageResetKey,
} from "../usePrecomputedGridPage"
import { BiothreatV2Body } from "./BiothreatV2Body"

export const BiothreatV2NwssView: React.FC = () => {
  const {
    addPinnedSignal,
    pinnedSignals,
    nwssFilters,
    dateRange,
    nwssSortModel,
    setNwssSortModel,
    pageSize,
  } = usePrecomputedBiothreatSignals()
  const pageResetKey = usePrecomputedGridPageResetKey(
    nwssFilters,
    dateRange,
    pageSize
  )
  const { page, setPage } = usePrecomputedGridPage(pageResetKey)
  const { data, isLoading } = usePrecomputedNwssSignals({
    page,
    sortModel: nwssSortModel,
  })
  // The map + rail summary reflect the whole filtered set, not the grid page.
  const { data: summaryData } = usePrecomputedNwssSignals({
    page: 0,
    sortModel: nwssSortModel,
    limit: PRECOMPUTED_MAP_RESULT_LIMIT,
  })

  const rows = data?.results ?? []
  const summaryRows = summaryData?.results ?? []
  const pinnedIds = useMemo(
    () => new Set(pinnedSignals.keys()),
    [pinnedSignals]
  )
  const columns = useMemo(
    () => getBiothreatV2NwssColumns({ pinnedIds, onPin: addPinnedSignal }),
    [addPinnedSignal, pinnedIds]
  )

  return (
    <BiothreatV2Body
      kind="nwss"
      rows={rows}
      summaryRows={summaryRows}
      rowCount={data?.count ?? 0}
      isLoading={isLoading && !data}
      page={page}
      onPageChange={setPage}
      sortModel={nwssSortModel}
      onSortModelChange={setNwssSortModel}
      sortableColumns={NWSS_SORTABLE_COLUMNS}
      defaultSortModel={DEFAULT_NWSS_SORT_MODEL}
      columns={columns}
      filters={<BiothreatV2NwssFilters />}
      renderDrawer={(signal, onClose) => (
        <NwssSignalDrawer signal={signal} onClose={onClose} variant="command" />
      )}
    />
  )
}
